import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { DateTime } from "luxon";

import {
  FnrPopover,
  FnrPopoverOption,
  FnrPopoverOptionsList,
} from "UI/FnrPopover2";
import {
  FnrToggle,
  FnrToggleChild,
  FnrToggleItems,
  FnrToggleTitle,
} from "UI/FnrToggle2";
import { HoverChild, HoverParent } from "UI/HoverChildren";

import { months } from "Utilities/Date";

export default function FnrDateInput({
  placeholder,
  style,
  setDate,
  initialDate,
  focusColor,
  killHoverStyles = false,
}) {
  const monthButtonRef = useRef();
  const dayButtonRef = useRef();
  const [month, setMonth] = useState();
  const [day, setDay] = useState();
  const [year, setYear] = useState("");
  const [daysInMonthArray, setDaysInMonthArray] = useState([]);

  const [showMonthPopover, setShowMonthPopover] = useState(false);
  const showDay = () => {
    // if no month is selected yet, open the month popover
    // once it is selected the day popover will open automatically, like normal
    if (!month) {
      setShowMonthPopover(true);
    } else {
      setShowDatePopover((current) => !current);
    }
  };
  const shouldCloseMonthPopover = (e) => {
    setShowMonthPopover(false);
  };
  const [showDatePopover, setShowDatePopover] = useState(false);
  const shouldCloseDatePopover = (e) => {
    setShowDatePopover(false);
  };

  useEffect(() => {
    if (!initialDate) return;
    let initialMonth, initialDay, initialYear;

    if (typeof initialDate === "string") {
      let date = DateTime.fromFormat(initialDate, "yyyy-MM-dd");
      if (!date.isValid) {
        console.error(
          "Invalid date format. Format must be yyyy-MM-dd. Or use an object with month, day, and year properties."
        );
        return;
      }
      initialMonth = date.month;
      initialDay = date.day;
      initialYear = date.year;
    } else if (typeof initialDate === "object") {
      initialMonth = initialDate.month;
      initialDay = initialDate.day;
      initialYear = initialDate?.year;
    } else {
      console.error(
        "initialDate has an invalid format. It's format must be a string with 'yyyy-MM-dd' format or an object with month and day properties.",
        initialDate
      );
    }

    if (initialYear) {
      setYear(initialYear);
    }
    if (initialMonth) {
      setMonth(months.find((m) => m.number === Number(initialMonth))?.name);
    }
    if (initialDay) {
      setDay(initialDay);
    }
  }, [initialDate]);

  useEffect(() => {
    if (!setDate) return;

    const dateObj = {};

    dateObj.month = months.find((m) => m.name === month)?.number || "";
    dateObj.day = day || "";
    dateObj.year = year || "";

    setDate(dateObj);

    // Aug 2021 → we’re doing a clean slate of all warnings and ain’t got time to look at this so that’s why this is disabled
    // eslint-disable-next-line
  }, [month, day, year]);

  const changeMonth = (month) => {
    setMonth(month);
    setResize((currentVal) => currentVal + 1);
  };

  const changeDay = (day) => {
    setDay(day);
    setResize((currentVal) => currentVal + 1);
  };

  const [resize, setResize] = useState(0);

  useEffect(() => {
    if (!month) return;

    const { daysInMonth } = months.find((m) => m.name === month);

    const daysArray = [];

    for (let i = 1; i <= daysInMonth; i++) {
      daysArray.push(i);
    }

    setDaysInMonthArray(daysArray);

    if (!day) setTimeout(() => setShowDatePopover(true), 300);

    // Aug 2021 → we’re doing a clean slate of all warnings and ain’t got time to look at this so that’s why this is disabled
    // eslint-disable-next-line
  }, [month]);

  const [yearIsFocused, setYearIsFocused] = useState(false);

  const yearFocused = () => setYearIsFocused(true);
  const yearUnfocused = () => setYearIsFocused(false);

  const updateYear = (e) => {
    let value = e.target.value;
    const year = value.replace(/[^\d]/g, "");
    setYear(year.slice(0, 4));
  };

  const yearInputRef = useRef();

  useEffect(() => {
    if (day) {
      setTimeout(() => {
        shouldCloseDatePopover();
        if (!year) yearInputRef?.current?.focus();
      }, 300);
    }

    // Aug 2021 → we’re doing a clean slate of all warnings and ain’t got time to look at this so that’s why this is disabled
    // eslint-disable-next-line
  }, [day]);

  return (
    <FnrToggle triggerResize={resize} style={style}>
      <FnrToggleTitle>{placeholder}</FnrToggleTitle>
      <FnrToggleItems>
        <FnrToggleChild
          ref={monthButtonRef}
          forceHover={showMonthPopover}
          killHoverStyles={killHoverStyles}
          style={{ flexGrow: 0 }}
        >
          <DateInputButton
            onClick={() => setShowMonthPopover((current) => !current)}
            role="button"
            tabIndex={0}
            focusColor={focusColor}
            style={{ color: month && style?.color ? style.color : "inherit" }}
          >
            {month || "Month"}
            <DateInputCaret className="far fa-angle-down" />
          </DateInputButton>
          <FnrPopoverOptionsList
            closeOnSelect
            buttonRef={monthButtonRef}
            show={showMonthPopover}
            onShouldClose={shouldCloseMonthPopover}
          >
            {months.map((m, index) => (
              <FnrPopoverOption
                key={index}
                selected={month === m.name}
                onClick={() => changeMonth(m.name)}
              >
                {m.name}
              </FnrPopoverOption>
            ))}
          </FnrPopoverOptionsList>
        </FnrToggleChild>
        <FnrToggleChild
          ref={dayButtonRef}
          forceHover={showDatePopover}
          killHoverStyles={true}
          style={{ flexGrow: 0 }}
        >
          <DateInputButton
            onClick={showDay}
            role="button"
            tabIndex={0}
            focusColor={focusColor}
            style={{ color: month && style?.color ? style.color : "inherit" }}
          >
            {day || "Day"}
            <DateInputCaret className="far fa-angle-down" />
          </DateInputButton>

          <FnrPopover
            buttonRef={dayButtonRef}
            show={showDatePopover}
            onShouldClose={shouldCloseDatePopover}
          >
            <HoverParent
              hoverStyle={{
                background: "rgba(255,255,255,.2)",
                borderRadius: 6,
              }}
            >
              <MonthDayPickerWrapper>
                {daysInMonthArray.map((d) => (
                  <MonthDay
                    key={d}
                    onClick={() => changeDay(d)}
                    selected={day === d}
                  >
                    <HoverChild>
                      <MonthDayInside>
                        <MonthDayNumber>{d}</MonthDayNumber>
                      </MonthDayInside>
                    </HoverChild>
                  </MonthDay>
                ))}
              </MonthDayPickerWrapper>
            </HoverParent>
          </FnrPopover>
        </FnrToggleChild>
        <FnrToggleChild forceHover={yearIsFocused} killHoverStyles={true}>
          <DateInputButton focusColor={focusColor}>
            <YearInput
              value={year}
              onChange={updateYear}
              onFocus={yearFocused}
              onBlur={yearUnfocused}
              placeholder="Year"
              // type="number"
              ref={yearInputRef}
              focusColor={focusColor}
              style={{ color: year && style?.color ? style.color : "inherit" }}
              pattern="\d{4}"
              autoFocus={false}
            />
          </DateInputButton>
        </FnrToggleChild>
      </FnrToggleItems>
    </FnrToggle>
  );
}

const DateInputButton = styled.div`
  padding: 8px;
  font-weight: 400;

  &:focus-within {
    outline: none;
    border-radius: 5px;
    box-shadow: ${(props) =>
      props.focusColor ? `inset 0 0 0 2px ${props.focusColor}` : "none"};
  }
`;

const MonthDayPickerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  width: 240px;
`;

const MonthDay = styled.div`
  width: calc(100% / 7);
  flex-grow: 0;
  flex-shrink: 0;
  z-index: 2;
  text-align: center;
  cursor: pointer;
  position: relative;
`;

const MonthDayInside = styled.div`
  padding-bottom: 100%;
`;

const MonthDayNumber = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const YearInput = styled.input`
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  appearance: none;
  color: inherit;
  font-size: inherit;
  outline: none;
  max-width: 50px;
  font-family: inherit;
  font-weight: inherit;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type="number"] {
    -moz-appearance: textfield;
  }
  &::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: inherit;
    opacity: 1; /* Firefox */
  }

  &:-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    color: #828c8d;
  }

  &::-ms-input-placeholder {
    /* Microsoft Edge */
    /* color: #828c8d; */
  }
`;

const DateInputCaret = styled.div`
  margin-left: 4px;
  opacity: 0.6;
`;
